import { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import saveFile from "../../../hooks/SaveFile";
import useInterviewProcessor from "../useInterviewProcessor";
import ProgressBar from "../ProgressBar";
import PrivacyStepsProgress from "../PrivacyStepsProgress";
import TranscriptReview from "../TranscriptReview";

// --- Audio chunking helpers ---

const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  let interleaved;
  if (numChannels === 2) {
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);
    interleaved = new Float32Array(left.length + right.length);
    let index = 0;
    for (let i = 0; i < left.length; i++) {
      interleaved[index++] = left[i];
      interleaved[index++] = right[i];
    }
  } else {
    interleaved = buffer.getChannelData(0);
  }

  const dataLength = interleaved.length * bytesPerSample;
  const arrayBuffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(arrayBuffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, "data");
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < interleaved.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, interleaved[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([view], { type: "audio/wav" });
};

const splitAudioIntoChunks = async (file, chunkDuration = 120) => {
  const audioContext = new AudioContext();
  const arrayBuffer = await file.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  const sampleRate = audioBuffer.sampleRate;
  const chunkSize = chunkDuration * sampleRate;
  const chunks = [];

  for (let start = 0; start < audioBuffer.length; start += chunkSize) {
    const end = Math.min(start + chunkSize, audioBuffer.length);
    const chunkBuffer = audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      end - start,
      sampleRate
    );
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      chunkBuffer.copyToChannel(
        audioBuffer.getChannelData(channel).slice(start, end),
        channel,
        0
      );
    }
    chunks.push(audioBufferToWav(chunkBuffer));
  }
  return chunks;
};

const uploadChunk = (blob, chunkFileName) =>
  new Promise((resolve) => {
    const file = new File([blob], chunkFileName, { type: "audio/wav" });
    saveFile({ target: { files: [file] } }, resolve);
  });

// --- Component ---

const AudioUploadTab = ({ interview, onPhaseChange, onAnalysisComplete, onAnalysisError }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const { privacySteps, phase, error, transcript, setTranscript, runPrivacy, save } =
    useInterviewProcessor(interview);

  // Let the detail page know when we leave the idle state (uploading counts
  // too) so it can hide the "Audio opnemen" heading and tab buttons.
  useEffect(() => {
    onPhaseChange?.(uploading ? "processing" : phase);
  }, [uploading, phase, onPhaseChange]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAudioFile(file);
    setAudioPreview(URL.createObjectURL(file));
    setUploadProgress(0);
  };

  const handleProcess = async () => {
    if (!audioFile) return;

    setUploading(true);
    setUploadProgress(1);

    let urls = [];
    try {
      const chunks = await splitAudioIntoChunks(audioFile, 120);
      for (let i = 0; i < chunks.length; i++) {
        const url = await uploadChunk(chunks[i], `${uuid()}_chunk_${i}.wav`);
        urls.push(url);
        setUploadProgress(Math.round(((i + 1) / chunks.length) * 100));
      }
    } catch (err) {
      console.error(err);
      setUploading(false);
      return;
    }

    setUploading(false);
    // Pass every chunk so the full recording is transcribed, not just the first.
    await runPrivacy(urls);
  };

  // The transcript is persisted at the start of save(), which unmounts this
  // tab. So we report the analysis result up to the detail page (which stays
  // mounted) instead of rendering it here.
  const handleSave = async () => {
    try {
      const result = await save();
      // Hand up the full analysis (quotes, anecdotes, TOC couplings, evidence,
      // tags, summary) — the detail page reviews it all as one proposal set.
      onAnalysisComplete?.(result);
    } catch (err) {
      onAnalysisError?.(err.message);
    }
  };

  return (
    <div className="audio-recorder-container">
        {phase === "idle" && !uploading && (
          <>
            <input type="file" accept="audio/*" onChange={handleFileChange} />
            {audioPreview && <audio controls src={audioPreview} />}
            {audioFile && uploadProgress === 0 && (
              <button onClick={handleProcess}>Verwerken</button>
            )}
          </>
        )}

        {(uploading || phase === "processing") && (
          <div>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <ProgressBar progress={uploadProgress} label="Uploaden" />
            )}
            {phase === "processing" && <PrivacyStepsProgress steps={privacySteps} />}
          </div>
        )}

        {phase === "review" && (
          <TranscriptReview transcript={transcript} setTranscript={setTranscript} onSave={handleSave} />
        )}

        {phase === "saving" && (
          <p className="privacy-steps-finalizing">Transcriptie opslaan…</p>
        )}

        {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
};

export default AudioUploadTab;
